const AD_ELEMENT_ID_PREFIX = "homepage-ad-";

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : null;
}

/**
 * Get element ID for an ad component
 * @param {string} componentName - The component name (e.g., "desktop_top_banner")
 * @returns {string} The element ID (e.g., "homepage-ad-desktop_top_banner")
 */
function getAdElementId(componentName) {
  return AD_ELEMENT_ID_PREFIX + componentName;
}

/**
 * Get component name from an ad element
 * @param {HTMLElement} element - The ad element
 * @returns {string} The component name (e.g., "desktop_top_banner")
 */
function getAdComponentName(element) {
  return element.id.replace(AD_ELEMENT_ID_PREFIX, '');
}

/**
 * Initialize ad tracking for homepage ad components using Intersection Observer
 * Tracks impressions when components become visible in the viewport
 * Only runs if ad components are present on the page
 * Ensures max 1 impression per component per page load
 */
export function initializeAdTracking() {
  // Check if any ad components exist on the page
  const hasAdComponents = document.querySelector('[id^="homepage-ad-"]');
  if (!hasAdComponents) {
    return; // No ads on page, skip tracking
  }

  // Create Intersection Observer to track when ads become visible
  const observer = new IntersectionObserver(function(entries) {
    // Collect all newly visible components in this batch
    const componentNamesToTrack = [];

    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        const componentName = getAdComponentName(entry.target);
        componentNamesToTrack.push(componentName);

        // Stop observing this element after tracking to prevent duplicates
        observer.unobserve(entry.target);
      }
    });

    // Send batched impression tracking if any components became visible
    if (componentNamesToTrack.length > 0) {
      const csrfToken = getCsrfToken();
      fetch("/homepage_ad_tracking/impressions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          component_names: componentNamesToTrack,
          campaign_id: window.HOMEPAGE_AD_CAMPAIGN_ID
        })
      }).catch(function(error) {
        if (window.Honeybadger) {
          Honeybadger.notify(error, {
            context: { message: "Ad impression tracking failed", component_names: componentNamesToTrack }
          });
        }
      });
    }
  }, {
    threshold: 0.5, // Track when 50% of the ad is visible
    rootMargin: '0px' // No margin, use exact viewport boundaries
  });

  // Observe all ad components
  // Use server-provided list of trackable components (synced from HomepageAd::TRACKABLE_COMPONENTS)
  const componentNames = window.HOMEPAGE_AD_TRACKABLE_COMPONENTS || [];
  componentNames.forEach(function(componentName) {
    const element = document.getElementById(getAdElementId(componentName));
    if (element) {
      observer.observe(element);
    }
  });
}

/**
 * Track ad click events
 * @param {string} componentName - The ad component name
 * @param {string} url - The destination URL
 */
export function trackAdClick(componentName, url) {
  // Create tracking data
  const trackingData = {
    component_name: componentName,
    url: url,
    campaign_id: window.HOMEPAGE_AD_CAMPAIGN_ID
  };

  const csrfToken = getCsrfToken();

  fetch("/homepage_ad_tracking/click", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
    },
    credentials: 'same-origin',
    body: JSON.stringify(trackingData),
    keepalive: true
  }).catch(function(error) {
    if (window.Honeybadger) {
      Honeybadger.notify(error, {
        context: { message: "Ad click tracking failed", component_name: componentName, url: url }
      });
    }
  });

  // Navigate to the URL
  if (url) {
    window.open(url, "_blank");
  }
}


/**
 * Initialize click event listeners for ad banners
 * Uses unobtrusive JavaScript with data attributes to prevent XSS
 * Only runs if clickable ads are present on the page
 */
function initializeClickTracking() {
  const clickableAds = document.querySelectorAll('.homepage-ad-clickable');

  if (clickableAds.length === 0) {
    return; // No clickable ads on page, skip setup
  }

  clickableAds.forEach(function(ad) {
    ad.addEventListener('click', function(event) {
      event.preventDefault();

      const componentName = this.dataset.adComponent;
      const url = this.dataset.adUrl;

      if (componentName && url) {
        trackAdClick(componentName, url);
      }
    });
  });
}

// Initialize click tracking when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeClickTracking);
} else {
  initializeClickTracking();
}
